import { Component, ChangeDetectionStrategy, input, signal, computed, inject, DestroyRef, effect, untracked, EventEmitter } from '@angular/core';
import { trigger, transition, query, style, stagger, animate } from '@angular/animations';
import { IBrandCard, IPlan } from '../../../types';
import { InViewportModule } from 'ng-in-viewport';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { backend } from '../../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { DateTime } from 'luxon';
import { debounceTime, map, merge, skip, switchMap, timer } from 'rxjs';
import { MenuCardComponent } from '../menu-card/menu-card.component';
import { CacheService } from '../../../auth.service';

@Component({
  selector: 'menu-grid',
  templateUrl: './menu-grid.component.html',
  styleUrls: ['./menu-grid.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InViewportModule, MenuCardComponent],
  animations: [
    trigger('listAnim', [
      transition(':enter, * => -1', []),
      transition(':increment', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(40px)' }),
          stagger(50, [
            animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
          ]),
        ], { optional: true })
      ]),
      transition(':decrement', [
        query(':leave', [
          stagger(50, [
            animate('300ms ease-out', style({ opacity: 0, transform: 'translateY(-100px)' })),
          ]),
        ])
      ]),
      transition('* => 0', [
        query(':leave', [
          stagger(50, [
            animate('300ms ease-out', style({ opacity: 0, transform: 'translateY(-100px)' })),
          ]),
        ])
      ]),
    ])
  ]
})
export class MenuGridComponent {
  http = inject(HttpClient);
  toast = inject(ToastrService);
  destroy = inject(DestroyRef);
  cache = inject(CacheService);

  sort = input<string>('popularity');
  query = input<string | undefined>();
  long = input<number | undefined>();
  lat = input<number | undefined>();

  page_limit = input<number>(0);
  limit = input<number>(30);
  paginated = input<boolean>(false);
  autoload = input<boolean>(true);//can load more

  page = signal<number>(0);
  is_final = signal<boolean>(false);
  brands = signal<IBrandCard[]>([]);
  loading = signal<boolean>(false);

  restart = new EventEmitter<void>();

  total = computed(() => this.brands().length);

  delay = computed(() => {
    const total = this.total();
    return total ? (50 * total) + 300 : 0;
  });

  constructor() {
    merge(toObservable(this.query), toObservable(this.sort)).pipe(takeUntilDestroyed(), debounceTime(100)).subscribe(change => {
      this.reset();
    });

    merge(this.restart, toObservable(this.page)).pipe(takeUntilDestroyed(), debounceTime(100)).subscribe(page => {
      this.getBrands();
    });

  }

  reset() {
    this.is_final.set(false);
    this.brands.set([]);
    this.page.set(0);
    this.restart.emit();
  }

  viewportTrigger(event: { target: Element, visible: boolean }) {
    // console.log('TRIGGER', event);
    // console.log('SKIP', this.loading() || this.is_final(), this.total());
    if (event.visible && this.autoload() && !this.loading() && !this.is_final()) {
      this.page.update(page => page + 1);
    }
  }

  getBrands() {
    if (this.is_final() || this.loading()) return;

    this.loading.set(true);

    const startTime = DateTime.now();

    const [page, limit, sort, query, long, lat, page_limit] = [
      this.page(), this.limit(), this.sort(), this.query(), this.long(), this.lat(), this.page_limit()
    ];

    const params: { [key: string]: string } = {
      page: String(page),
      limit: String(limit),
      sort,
      tags: 'menu'
    };

    if (query)
      params.query = query;

    if (long && lat) {
      params.long = String(long);
      params.lat = String(lat);
    }

    this.http.get<IBrandCard[] | { brands: IBrandCard[], plans: IPlan[] }>(backend + 'v3/brands/featured', { params }).pipe(
      takeUntilDestroyed(this.destroy),
      switchMap(brands => timer(this.page() ? 0 : (this.delay() + startTime.diffNow('milliseconds').as('milliseconds'))).pipe(map(() => brands)))
    ).subscribe({
      next: value => {

        // console.log(this.delay(), startTime.diffNow('milliseconds').as('milliseconds'));

        const brands = 'brands' in value ? value.brands : value;

        if('plans' in value){
          this.cache.registerPlans(value.plans);
        }

        this.brands.update(existing_brands => [...existing_brands, ...brands]);
        if (brands.length < limit || (page_limit && page <= page_limit)) {
          this.is_final.set(true);
        }

        this.loading.set(false);

      },
      error: error => {
        error = error?.error || error;
        this.toast.error(error.message);
        this.loading.set(false);
      }
    });
  }

}
